/**
 * Gráficos SVG leves, sem dependências externas.
 * Escalam com a largura do container (viewBox + width:100%) e respeitam o
 * tema claro/escuro via variáveis CSS (--border, --muted, --surface).
 */

export interface Serie {
  key: string;
  label: string;
  color: string;
}

const VB_W = 560;

/** Arredonda um máximo para um valor "bonito" (10, 20, 50, 100...). */
function niceMax(value: number): number {
  if (value <= 0) return 1;
  const pow = Math.pow(10, Math.floor(Math.log10(value)));
  const n = value / pow;
  const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
  return step * pow;
}

function ticks(max: number, count = 4): number[] {
  return Array.from({ length: count + 1 }, (_, i) => (max / count) * i);
}

/* ----------------------------- Gráfico de linhas ----------------------------- */
export function LineChart({
  data,
  xKey,
  series,
  height = 260,
}: {
  data: Record<string, any>[];
  xKey: string;
  series: Serie[];
  height?: number;
}) {
  const padL = 38, padR = 16, padT = 14, padB = 30;
  const innerW = VB_W - padL - padR;
  const innerH = height - padT - padB;
  const n = data.length;

  const max = niceMax(
    Math.max(1, ...data.flatMap((d) => series.map((s) => Number(d[s.key]) || 0))),
  );
  const xAt = (i: number) =>
    n <= 1 ? padL + innerW / 2 : padL + (i / (n - 1)) * innerW;
  const yAt = (v: number) => padT + innerH - (v / max) * innerH;
  const showEvery = n > 8 ? 2 : 1;

  return (
    <div className="chart">
      <Legend series={series} />
      <svg viewBox={`0 0 ${VB_W} ${height}`} className="chart-svg" role="img">
        {ticks(max).map((t, i) => (
          <g key={i}>
            <line x1={padL} y1={yAt(t)} x2={VB_W - padR} y2={yAt(t)} className="chart-grid" />
            <text x={padL - 6} y={yAt(t) + 3} className="chart-axis" textAnchor="end">
              {Math.round(t)}
            </text>
          </g>
        ))}
        {data.map((d, i) =>
          i % showEvery === 0 ? (
            <text key={i} x={xAt(i)} y={height - 10} className="chart-axis" textAnchor="middle">
              {String(d[xKey])}
            </text>
          ) : null,
        )}
        {series.map((s) => {
          const pts = data.map((d, i) => `${xAt(i)},${yAt(Number(d[s.key]) || 0)}`).join(" ");
          return (
            <g key={s.key}>
              <polyline points={pts} fill="none" stroke={s.color} strokeWidth={2.5}
                strokeLinejoin="round" strokeLinecap="round" />
              {data.map((d, i) => (
                <circle key={i} cx={xAt(i)} cy={yAt(Number(d[s.key]) || 0)} r={3} fill={s.color}>
                  <title>{`${s.label} — ${d[xKey]}: ${d[s.key]}`}</title>
                </circle>
              ))}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

/* ----------------------------- Gráfico de barras ----------------------------- */
export function BarChart({
  data,
  xKey,
  series,
  height = 260,
  max: maxOverride,
}: {
  data: Record<string, any>[];
  xKey: string;
  series: Serie[];
  height?: number;
  max?: number;
}) {
  const padL = 38, padR = 16, padT = 14, padB = 30;
  const innerW = VB_W - padL - padR;
  const innerH = height - padT - padB;
  const n = data.length || 1;

  const max =
    maxOverride ??
    niceMax(Math.max(1, ...data.flatMap((d) => series.map((s) => Number(d[s.key]) || 0))));
  const groupW = innerW / n;
  const barGap = 6;
  const barW = Math.max(4, (groupW * 0.62 - barGap * (series.length - 1)) / series.length);
  const yAt = (v: number) => padT + innerH - (v / max) * innerH;

  return (
    <div className="chart">
      <Legend series={series} />
      <svg viewBox={`0 0 ${VB_W} ${height}`} className="chart-svg" role="img">
        {ticks(max).map((t, i) => (
          <g key={i}>
            <line x1={padL} y1={yAt(t)} x2={VB_W - padR} y2={yAt(t)} className="chart-grid" />
            <text x={padL - 6} y={yAt(t) + 3} className="chart-axis" textAnchor="end">
              {Math.round(t)}
            </text>
          </g>
        ))}
        {data.map((d, i) => {
          const gx = padL + i * groupW + groupW / 2;
          const totalW = series.length * barW + (series.length - 1) * barGap;
          const startX = gx - totalW / 2;
          return (
            <g key={i}>
              {series.map((s, si) => {
                const v = Number(d[s.key]) || 0;
                const x = startX + si * (barW + barGap);
                const y = yAt(v);
                return (
                  <rect key={s.key} x={x} y={y} width={barW} height={padT + innerH - y}
                    rx={3} fill={s.color}>
                    <title>{`${s.label} — ${d[xKey]}: ${d[s.key]}`}</title>
                  </rect>
                );
              })}
              <text x={gx} y={height - 10} className="chart-axis" textAnchor="middle">
                {String(d[xKey])}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

/* ------------------------------ Gráfico de pizza ------------------------------ */
export function PieChart({
  data,
  valueKey,
  labelKey,
  colors,
}: {
  data: Record<string, any>[];
  valueKey: string;
  labelKey: string;
  colors: string[];
}) {
  const size = 220, r = 96, cx = size / 2, cy = size / 2;
  const total = data.reduce((acc, d) => acc + (Number(d[valueKey]) || 0), 0) || 1;

  let angle = -Math.PI / 2; // começa no topo
  const slices = data.map((d, i) => {
    const value = Number(d[valueKey]) || 0;
    const frac = value / total;
    const start = angle;
    const end = angle + frac * Math.PI * 2;
    angle = end;
    const large = end - start > Math.PI ? 1 : 0;
    const x1 = cx + r * Math.cos(start), y1 = cy + r * Math.sin(start);
    const x2 = cx + r * Math.cos(end), y2 = cy + r * Math.sin(end);
    const path =
      data.length === 1
        ? `M ${cx} ${cy} m ${-r} 0 a ${r} ${r} 0 1 0 ${r * 2} 0 a ${r} ${r} 0 1 0 ${-r * 2} 0`
        : `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`;
    return {
      path,
      color: colors[i % colors.length],
      label: String(d[labelKey]),
      value,
      pct: Math.round(frac * 100),
    };
  });

  return (
    <div className="chart chart-pie">
      <svg viewBox={`0 0 ${size} ${size}`} className="chart-svg-pie" role="img">
        {slices.map((s, i) => (
          <path key={i} d={s.path} fill={s.color} stroke="var(--surface)" strokeWidth={2}>
            <title>{`${s.label}: ${s.value} (${s.pct}%)`}</title>
          </path>
        ))}
      </svg>
      <ul className="pie-legend">
        {slices.map((s, i) => (
          <li key={i}>
            <span className="pie-dot" style={{ background: s.color }} />
            <span className="pie-name">{s.label}</span>
            <span className="pie-val">{s.value} · {s.pct}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* --------------------------- Barras horizontais (ranking) --------------------------- */
export function HBars({
  data,
  labelKey,
  valueKey,
  color = "#0c7a3f",
  format,
}: {
  data: Record<string, any>[];
  labelKey: string;
  valueKey: string;
  color?: string;
  format?: (v: number) => string;
}) {
  if (data.length === 0) return <div className="empty">Sem dados.</div>;
  const max = Math.max(1, ...data.map((d) => Number(d[valueKey]) || 0));
  return (
    <div className="hbars">
      {data.map((d, i) => {
        const v = Number(d[valueKey]) || 0;
        return (
          <div className="hbar-row" key={i}>
            <span className="hbar-label" title={String(d[labelKey])}>
              {String(d[labelKey])}
            </span>
            <div className="hbar-track">
              <div
                className="hbar-fill"
                style={{ width: `${(v / max) * 100}%`, background: color }}
              />
            </div>
            <span className="hbar-val">{format ? format(v) : v}</span>
          </div>
        );
      })}
    </div>
  );
}

/* --------------------------------- Legenda --------------------------------- */
function Legend({ series }: { series: Serie[] }) {
  if (series.length < 2) return null;
  return (
    <div className="chart-legend">
      {series.map((s) => (
        <span key={s.key} className="legend-item">
          <span className="legend-dot" style={{ background: s.color }} />
          {s.label}
        </span>
      ))}
    </div>
  );
}
